/* @flow */
import * as React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useId } from 'react-aria';

// Styles
import styles from './SocialSharingModal.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Hooks
import useTopic from 'src/shared/domain/topic/topic/useTopic';
import useShareToTwitter from 'src/shared/app/social/hooks/useShareToTwitter';
import useShareToLinkedIn from 'src/shared/app/social/hooks/useShareToLinkedIn';

// Utils
import messages from 'src/shared/domain/topic/l10n/topicL10n';
import { phablet } from '@braindate/ui/lib/token/mediaQuery';
import { clearModal } from 'src/shared/app/base/modal/slice/modalSlice';
import { fetchTopic } from 'src/shared/domain/topic/action/topicActions';
import { getTopicPermalink } from '@braindate/domain/lib/topic/util';

// Components
import Modal from 'src/shared/app/base/modal/component/Modal';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import { IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { ContentCopy, Twitter, LinkedIn } from '@mui/icons-material';
import ConditionalWrapper from 'src/shared/ui/component/misc/ConditionalWrapper';

const useStyles = createUseThemeStyles(styles);

type Props = {
  topicId: number,
};

const SocialSharingModal = ({ topicId }: Props) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const isLarge = useMediaQuery(phablet);
  const mediaId = useId();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [showCopied, setShowCopied] = React.useState<boolean>(false);
  const [couldNotCopy, setCouldNotCopy] = React.useState<boolean>(false);

  const topic = useTopic(topicId);
  const topicPermalink = !!topic && getTopicPermalink(topic);

  const shareToTwitter = useShareToTwitter(topic);
  const shareToLinkedIn = useShareToLinkedIn(topic);

  let timerId: number | void;

  const clearTimeout = () => {
    if (!timerId) return;
    window.clearTimeout(timerId);
    timerId = undefined;
  };

  const showCopiedRetroaction = () => {
    setShowCopied(true);
    clearTimeout();
    timerId = window.setTimeout(() => setShowCopied(false), 3000);
  };

  React.useEffect(() => () => clearTimeout(), []);

  React.useEffect(() => {
    dispatch(fetchTopic(topicId));
  }, [topicId]);

  const fallbackHandleCopyLink = () => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    input.focus();
    input.select();

    const isTextCopied = document.execCommand('copy');
    if (!isTextCopied) {
      return setCouldNotCopy(true);
    }

    showCopiedRetroaction();
  };

  const handleCopyLink = async () => {
    setCouldNotCopy(false);

    if (!navigator.clipboard) {
      fallbackHandleCopyLink();
      return;
    }

    if (!topicPermalink) return;
    try {
      await navigator.clipboard.writeText(topicPermalink);
      showCopiedRetroaction();
    } catch (err) {
      setCouldNotCopy(true);
    }
  };

  const handleClose = () => dispatch(clearModal());

  return (
    <Modal
      title={intl.formatMessage(messages.share)}
      width={isLarge ? 420 : '90vw'}
      handleClose={handleClose}
      showClose
      hideActions
      content={
        <div className={classes.root}>
          <div className={classes.icons}>
            <SecondaryButton
              withEvenSides
              layoutClass={classes.iconButton}
              onClick={shareToTwitter}
              aria-label="Twitter"
            >
              <Twitter className={classNames(classes.icon, classes.twitter)} />
            </SecondaryButton>

            <SecondaryButton
              withEvenSides
              layoutClass={classes.iconButton}
              onClick={shareToLinkedIn}
              aria-label="LinkedIn"
            >
              <LinkedIn
                className={classNames(classes.icon, classes.linkedIn)}
              />
            </SecondaryButton>
          </div>

          <label htmlFor={mediaId} className={classes.copy}>
            {intl.formatMessage(messages.copyLink)}
          </label>

          {!!topicPermalink && (
            <div className={classes.inputWrapper}>
              <input
                className={classes.input}
                value={topicPermalink}
                ref={inputRef}
                id={mediaId}
              />
              <ConditionalWrapper
                condition={showCopied}
                wrapper={(children) => (
                  <Tooltip
                    open
                    title={intl.formatMessage(messages.copiedToClipboard)}
                    placement="top"
                    arrow
                  >
                    {children}
                  </Tooltip>
                )}
              >
                <IconButton
                  onClick={handleCopyLink}
                  size="small"
                  aria-label={intl.formatMessage(messages.copyLinkButton)}
                >
                  <ContentCopy classes={{ root: classes.adornment }} />
                </IconButton>
              </ConditionalWrapper>
            </div>
          )}

          {couldNotCopy && (
            <p className={classes.error}>
              {intl.formatMessage(messages.couldNotCopy)}
            </p>
          )}
        </div>
      }
    />
  );
};

export default React.memo<Props>(SocialSharingModal);
